import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Send, Circle, ArrowLeft, Info, Clock, CheckCircle, ExternalLink } from 'lucide-react';
import { useSavChat } from '@/shared/hooks/useSavChat';
import { useOrder } from '@/shared/hooks/useOrder';
import { useChatPresence } from '@/shared/hooks/useChatPresence';
import { useAuth } from '@/app/providers';
import {
  ChatStatusColors,
  ChatStatus,
  SenderType,
} from '@/models/savChat';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChatInfoSidebar } from './ChatInfoSidebar';
import { useTranslation } from 'react-i18next';
import { getCustomerOrgByEmail } from '@/services/firebase/customer-orgs-repository';
import { CustomerDetail } from './customers';
import { toast } from 'react-hot-toast';

interface AdminSavChatProps {
  chatId: string;
  organizationId: string;
  onUpdateStatus: (chatId: string, status: ChatStatus) => Promise<void>;
  onBack?: () => void;
}

export const AdminSavChat: React.FC<AdminSavChatProps> = ({
  chatId,
  organizationId,
  onUpdateStatus,
  onBack,
}) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const { chat, messages, loading, sendMessage, markAsRead } = useSavChat(chatId, SenderType.ADMIN);
  const { order, loading: loadingOrder } = useOrder(chat?.orderId || null);

  // Mémoriser le nom de l'admin pour éviter les re-renders
  const adminName = useMemo(() => {
    return currentUser?.displayName || 'Support';
  }, [currentUser?.displayName]);

  const { otherUserPresence, setTyping } = useChatPresence(
    chatId,
    currentUser?.uid || null,
    adminName,
    'admin'
  );
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUpdatingStatus] = useState(false);
  const [showMobileInfo, setShowMobileInfo] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // États pour la fiche client (customerOrgs)
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [customerData, setCustomerData] = useState<Awaited<ReturnType<typeof getCustomerOrg>>>(null);
  const [loadingCustomerData, setLoadingCustomerData] = useState(false);

  const handleOpenCustomerDetail = async () => {
    const customerEmail = order?.orderInfo?.email || chat?.customerEmail;
    if (!customerEmail) {
      toast.error("Email du client non disponible.");
      return;
    }

    setLoadingCustomerData(true);
    try {
      const emailToSearch = customerEmail.trim().toLowerCase();
      const co = await getCustomerOrgByEmail(organizationId, emailToSearch);
      if (co) {
        setCustomerData(co);
        setShowCustomerDetail(true);
      } else {
        toast.error("Impossible de trouver les données détaillées de ce client.");
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des données client:", error);
      toast.error("Erreur lors de la récupération des données client.");
    } finally {
      setLoadingCustomerData(false);
    }
  };
  const typingDebounceRef = useRef<NodeJS.Timeout>();

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Marquer les messages comme lus quand on ouvre le chat ou quand de nouveaux messages arrivent
  useEffect(() => {
    if (chatId && chat && chat.unreadByAdmin > 0) {
      markAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, chat?.unreadByAdmin]); // Se déclenche quand le chat change ou quand unreadByAdmin change

  // Marquer comme lu aussi quand la fenêtre reprend le focus
  useEffect(() => {
    const handleFocus = () => {
      if (chatId && chat && chat.unreadByAdmin > 0) {
        markAsRead();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, chat?.unreadByAdmin]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      await sendMessage(newMessage);
      setNewMessage('');
      setTyping(false); // Stop typing indicator after sending
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleMessageChange = (value: string) => {
    setNewMessage(value);

    // Debounce typing indicator pour éviter trop d'appels
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
    }

    if (value.trim().length > 0) {
      typingDebounceRef.current = setTimeout(() => {
        setTyping(true);
      }, 300); // 300ms debounce
    } else {
      setTyping(false);
    }
  };

  if (loading || !chat) {
    return (
      <div className="flex items-center justify-center h-full bg-background-secondary/30">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-border border-t-primary rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm text-foreground-secondary">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-full w-full overflow-hidden">
      {/* Section Chat (gauche/centre) */}
      <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0">
        {/* Header */}
        <div className="pt-6 px-4 pb-4 border-b border-border-light bg-surface flex-shrink-0">
          <div className="flex items-start gap-3">
            {/* Bouton retour mobile */}
            {onBack && (
              <button
                onClick={onBack}
                className="md:hidden flex-shrink-0 p-2 -ml-2 hover:bg-background-secondary rounded-lg transition-colors"
                aria-label="Retour à la liste"
              >
                <ArrowLeft className="h-5 w-5 text-foreground" />
              </button>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-foreground truncate">{chat.customerName}</h2>
                <button
                  onClick={handleOpenCustomerDetail}
                  disabled={loadingCustomerData || !chat?.customerEmail}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark transition-colors px-1.5 py-0.5 rounded hover:bg-primary-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('admin.savChat.admin.info.customerFile')}
                >
                  <ExternalLink size={12} />
                  <span className="hidden sm:inline">{t('admin.savChat.admin.info.customerFile')}</span>
                </button>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-xs text-foreground-secondary truncate">{chat.customerEmail}</p>
                {otherUserPresence && (
                  <>
                    <span className="text-foreground-secondary hidden sm:inline">•</span>
                    <div className="hidden sm:flex items-center gap-1.5">
                      <Circle
                        className={`h-2 w-2 ${otherUserPresence.isOnline
                          ? 'fill-green-500 text-green-500'
                          : 'fill-gray-400 text-gray-400'
                          }`}
                      />
                      <span className="text-xs text-foreground-secondary">
                        {otherUserPresence.isOnline ? t('admin.savChat.admin.online') : t('admin.savChat.admin.offline')}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium ${ChatStatusColors[chat.status].bg
                  } ${ChatStatusColors[chat.status].text}`}
              >
                {t(`admin.savChat.status.${chat.status.toLowerCase()}`)}
              </span>

              {/* Bouton Info mobile */}
              <button
                onClick={() => setShowMobileInfo(!showMobileInfo)}
                className="md:hidden flex-shrink-0 p-2 hover:bg-background-secondary rounded-lg transition-colors"
                aria-label="Informations"
              >
                <Info className="h-5 w-5 text-foreground" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-background-secondary/30 custom-scrollbar min-h-0">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-foreground-secondary">{t('admin.savChat.admin.noMessages')}</p>
            </div>
          ) : (
            <>
              {messages.map((message) => {
                if (message.senderType === SenderType.SYSTEM) {
                  return (
                    <div key={message.id} className="flex justify-center my-4">
                      <div className="bg-background-secondary/50 px-4 py-2 rounded-lg max-w-[85%] text-center border border-border-light">
                        <p className="text-xs text-foreground-secondary whitespace-pre-wrap">
                          {message.message}
                        </p>
                      </div>
                    </div>
                  );
                }

                const isAdmin = message.senderType === SenderType.ADMIN;
                return (
                  <div
                    key={message.id}
                    className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-xl px-3 py-2 ${isAdmin
                        ? 'bg-primary text-white'
                        : 'bg-surface border border-border'
                        }`}
                    >
                      <p className={`text-sm ${isAdmin ? 'text-white' : 'text-foreground'}`}>
                        {message.message}
                      </p>
                      <div
                        className={`flex items-center gap-1.5 mt-1 text-xs ${isAdmin ? 'text-white/70' : 'text-foreground-secondary'
                          }`}
                      >
                        <span className="font-medium">{message.senderName}</span>
                        <span>•</span>
                        <span>{format(message.createdAt, 'HH:mm', { locale: fr })}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Typing indicator */}
              {otherUserPresence?.isTyping && (
                <div className="flex justify-start">
                  <div className="max-w-[75%] rounded-xl px-3 py-2 bg-surface border border-border">
                    <div className="flex items-center gap-1.5">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-foreground-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-foreground-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-foreground-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span className="text-xs text-foreground-secondary ml-2">
                        {t('admin.savChat.admin.typing', { name: otherUserPresence.userName })}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Status Actions */}
        <div className="px-3 py-2 border-t border-border-light bg-surface flex items-center gap-2 flex-shrink-0">
          {chat.status !== ChatStatus.IN_PROGRESS && (
            <button
              onClick={() => onUpdateStatus(chat.id, ChatStatus.IN_PROGRESS)}
              disabled={isUpdatingStatus}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-warning/10 text-warning-dark hover:bg-warning/20 rounded-lg transition-colors font-medium"
            >
              <Clock className="w-3.5 h-3.5" />
              <span>{t('admin.savChat.admin.passToInProgress')}</span>
            </button>
          )}
          {chat.status !== ChatStatus.CLOSED && (
            <button
              onClick={() => onUpdateStatus(chat.id, ChatStatus.CLOSED)}
              disabled={isUpdatingStatus}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-success/10 text-success-dark hover:bg-success/20 rounded-lg transition-colors font-medium"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              <span>{t('admin.savChat.admin.markAsResolvedLong')}</span>
            </button>
          )}
        </div>

        {/* Input form */}
        {chat.status !== ChatStatus.CLOSED ? (
          <form onSubmit={handleSendMessage} className="p-3 pt-0 bg-surface flex-shrink-0">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => handleMessageChange(e.target.value)}
                  placeholder={t('admin.savChat.admin.replyPlaceholder')}
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-foreground-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                  disabled={isSending}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!newMessage.trim() || isSending}
                className="px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <Send className="h-4 w-4" />
                <span className="hidden sm:inline">{isSending ? t('admin.savChat.admin.sending') : t('admin.savChat.admin.send')}</span>
              </button>
            </div>
            <p className="text-xs text-foreground-secondary mt-1.5">
              {t('admin.savChat.admin.inputHelp')}
            </p>
          </form>
        ) : (
          <div className="p-3 pt-0 bg-background-secondary/50 flex-shrink-0">
            <div className="flex items-center justify-center gap-2 text-sm text-foreground-secondary p-2 rounded-lg border border-border-light border-dashed">
              <span>{t('admin.savChat.chatClosed')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Info Sidebar (droite) - Hidden on mobile, visible sur md+ */}
      <div className="hidden md:flex md:w-[360px] lg:w-[400px] flex-shrink-0 h-full border-l border-border overflow-hidden bg-surface">
        <ChatInfoSidebar
          chat={chat}
          order={order}
          loadingOrder={loadingOrder}
          organizationId={organizationId}
          onUpdateStatus={onUpdateStatus}
          isUpdatingStatus={isUpdatingStatus}
        />
      </div>

      {/* Mobile Info Sidebar - Overlay en plein écran */}
      {showMobileInfo && (
        <div className="md:hidden fixed inset-0 z-50 bg-background">
          <div className="h-full flex flex-col">
            {/* Header avec bouton fermer */}
            <div className="p-3 border-b border-border-light bg-surface flex-shrink-0 flex items-center justify-between">
              <h3 className="text-base font-bold text-foreground">{t('admin.savChat.admin.info.title')}</h3>
              <button
                onClick={() => setShowMobileInfo(false)}
                className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <ArrowLeft className="h-5 w-5 text-foreground" />
              </button>
            </div>
            {/* Sidebar content */}
            <div className="flex-1 overflow-hidden">
              <ChatInfoSidebar
                chat={chat}
                order={order}
                loadingOrder={loadingOrder}
                organizationId={organizationId}
                onUpdateStatus={onUpdateStatus}
                isUpdatingStatus={isUpdatingStatus}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Fiche Client */}
      {customerData && (
        <CustomerDetail
          customer={customerData}
          onClose={() => {
            setShowCustomerDetail(false);
            setCustomerData(null);
          }}
          isOpen={showCustomerDetail}
          period={30}
        />
      )}
    </div>
  );
};

